// WorkProcessComponent.js - Updated with Increased Duration for Mobile
import React, { useState, useEffect, useRef } from 'react';
import { FileText, Settings, Palette, Code, TestTube, Rocket, Wrench } from 'lucide-react';
import colors from '../ColorConfig/colorconfig';
import WorkProcessHeader from './wpheader';
import DesktopProcessFlow from './dprocessflow';
import MobileProcessFlow from './mprocessflow';
import BackgroundElements from './bgelements';

const { primary, bodyBlack } = colors;

const WorkProcessComponent = () => {
  const [isSectionInView, setIsSectionInView] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState([]);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [animationStarted, setAnimationStarted] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [scrollDirection, setScrollDirection] = useState('down');
  const [shouldAnimateOut, setShouldAnimateOut] = useState(false);
  const sectionRef = useRef(null);
  const lastScrollY = useRef(0);
  const animationTimeouts = useRef([]);
  const isAnimating = useRef(false);
  const lastHiddenStep = useRef(null);
  const sectionVisibility = useRef(0);
  const [isMobileView, setIsMobileView] = useState(false);

  const processSteps = [
    {
      id: 1,
      title: "Requirement Analysis",
      description: "We gather and analyze your project requirements to ensure clear understanding.",
      icon: FileText,
      color: "#00bcd4",
    },
    {
      id: 2,
      title: "Planning & Strategy", 
      description: "Detailed planning with milestones and resource allocation for smooth execution.",
      icon: Settings,
      color: "#006ec899",
    },
    {
      id: 3,
      title: "UI/UX & System Design",
      description: "Designing intuitive interfaces and robust system architecture.",
      icon: Palette,
      color: "#2440dfff",
    },
    {
      id: 4,
      title: "Development",
      description: "Bringing designs to life with clean, scalable code.",
      icon: Code,
      color: "#673ab7",
    },
    {
      id: 5,
      title: "Testing & QA",
      description: "Comprehensive testing to ensure a high-quality, bug-free product.",
      icon: TestTube,
      color: "#9c27b0",
    },
    {
      id: 6,
      title: "Deployment",
      description: "Seamless deployment, ensuring stability and performance.",
      icon: Rocket,
      color: "#e91e63",
    },
    {
      id: 7,
      title: "Support & Maintenance",
      description: "Ongoing support to adapt to evolving needs and technologies.",
      icon: Wrench,
      color: "#f44336",
    }
  ];

  // Check if mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 992);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Handle scroll with progressive step hiding
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const sectionElement = sectionRef.current;
      
      if (!sectionElement) return;

      const sectionRect = sectionElement.getBoundingClientRect();
      const sectionTop = sectionRect.top;
      const sectionBottom = sectionRect.bottom;
      const windowHeight = window.innerHeight;

      // Calculate how much of the section is visible (0 to 1)
      const visibleHeight = Math.min(sectionBottom, windowHeight) - Math.max(sectionTop, 0);
      const sectionHeight = sectionRect.height;
      const visibilityRatio = Math.max(0, Math.min(1, visibleHeight / sectionHeight));
      
      sectionVisibility.current = visibilityRatio;

      // Check if section is in viewport
      const isInView = sectionTop < windowHeight * 0.8 && sectionBottom > windowHeight * 0.2;
      setIsSectionInView(isInView);

      if (currentScrollY > lastScrollY.current) {
        // Scrolling DOWN
        setScrollDirection('down');
        setShouldAnimateOut(false);
        
        if (isInView && !isAnimating.current && visibleSteps.length === 0) {
          startScrollDownAnimation();
        } else if (isInView && visibleSteps.length < processSteps.length && !isAnimating.current) {
          continueScrollDownAnimation();
        }
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling UP
        setScrollDirection('up');
        
        if (isInView && visibleSteps.length > 0 && !isAnimating.current) {
          handleProgressiveScrollUp(visibilityRatio);
        } else if (!isInView && visibleSteps.length > 0) {
          hideAllSteps();
        }
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleSteps]);

  // Clear all timeouts on unmount
  useEffect(() => {
    return () => {
      animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
      animationTimeouts.current = [];
    };
  }, []);

  // Intersection Observer for section visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;
        setIsSectionInView(isIntersecting);
        
        if (isIntersecting && scrollDirection === 'down' && !isAnimating.current && visibleSteps.length === 0) {
          startScrollDownAnimation();
        } else if (!isIntersecting && scrollDirection === 'up' && visibleSteps.length > 0) {
          hideAllSteps();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [scrollDirection, visibleSteps]);

  const handleProgressiveScrollUp = (visibilityRatio) => {
    // Calculate how many steps should remain visible based on section visibility
    const totalSteps = processSteps.length;
    const stepsToKeep = Math.max(1, Math.floor(visibilityRatio * totalSteps));
    const targetVisibleSteps = Math.min(stepsToKeep, visibleSteps.length);
    
    // If we need to hide steps
    if (targetVisibleSteps < visibleSteps.length) {
      setShouldAnimateOut(true);
      isAnimating.current = true;
      
      // Get the steps that should be hidden (from the bottom - highest indexes first)
      const stepsToHide = [...visibleSteps]
        .sort((a, b) => b - a) // Sort in descending order
        .slice(0, visibleSteps.length - targetVisibleSteps); // Get the steps to hide
      
      console.log(`Hiding steps: ${stepsToHide.join(', ')} based on visibility ratio: ${visibilityRatio.toFixed(2)}`);
      
      // Hide steps with a small delay between each (reverse order - highest index first)
    const hideDelay = isMobileView ? 100 : 150; // Faster hiding for mobile
      stepsToHide.forEach((stepIndex, index) => {
        const timeout = setTimeout(() => {
          setVisibleSteps(prev => {
            const newSteps = prev.filter(step => step !== stepIndex);
            
            // If this is the last step to hide, mark animation as complete
            if (index === stepsToHide.length - 1) {
              isAnimating.current = false;
              setShouldAnimateOut(false);
              console.log('Progressive hiding completed');
            }
            
            return newSteps;
          });
          lastHiddenStep.current = stepIndex;
        }, index * hideDelay);
        animationTimeouts.current.push(timeout);
      });
    } else {
      setShouldAnimateOut(false);
    }
  };

  const startScrollDownAnimation = () => {
    if (isAnimating.current) return;
    
    isAnimating.current = true;
    clearAllTimeouts();
    setShouldAnimateOut(false);
    setAnimationStarted(false);

    console.log('Starting scroll down animation - Mobile:', isMobileView);

   // Calculate delays based on device type
const initialDelay = isMobileView ? 150 : 200; // Faster initial delay for mobile
const stepDelay = isMobileView ? 200 : 200; // Faster delay between steps for mobile
const connectionDelay = isMobileView ? 400 : 500; // Faster connection animation delay for mobile

    // Show steps sequentially from step 1 to step 7
    processSteps.forEach((_, index) => {
      const timeout = setTimeout(() => {
        setVisibleSteps(prev => {
          const newSteps = [...new Set([...prev, index])];
          // If all steps are visible, mark animation as complete
          if (newSteps.length === processSteps.length) {
            isAnimating.current = false;
            console.log('Scroll down animation completed');
          }
          return newSteps;
        });
      }, initialDelay + (index * stepDelay));
      animationTimeouts.current.push(timeout);
    });

    // Start connection animation after all steps are visible
    const connectionTimeout = setTimeout(() => {
      setAnimationStarted(true);
    }, initialDelay + (processSteps.length * stepDelay) + connectionDelay);
    animationTimeouts.current.push(connectionTimeout);
  };

  const continueScrollDownAnimation = () => {
    if (isAnimating.current) return;
    
    isAnimating.current = true;
    
    // Continue showing steps that aren't visible yet
    const nextStepIndex = visibleSteps.length;
    if (nextStepIndex < processSteps.length) {
      const delay = isMobileView ? 400 : 200; // Increased delay for mobile
      const timeout = setTimeout(() => {
        setVisibleSteps(prev => {
          const newSteps = [...new Set([...prev, nextStepIndex])];
          isAnimating.current = false;
          return newSteps;
        });
      }, delay);
      animationTimeouts.current.push(timeout);
    }
  };

  const hideAllSteps = () => {
    if (isAnimating.current) return;
    
    isAnimating.current = true;
    clearAllTimeouts();
    setAnimationStarted(false);
    setShouldAnimateOut(true);

    console.log('Hiding all steps - section out of view');

    // Hide all remaining steps in reverse order (highest index first)
    const stepsToHide = [...visibleSteps].sort((a, b) => b - a);
    const hideDelay = isMobileView ? 80 : 120; // Faster hiding for mobile
    
    stepsToHide.forEach((stepIndex, index) => {
      const timeout = setTimeout(() => {
        setVisibleSteps(prev => {
          const newSteps = prev.filter(step => step !== stepIndex);
          // If all steps are hidden, mark animation as complete
          if (newSteps.length === 0) {
            isAnimating.current = false;
            setShouldAnimateOut(false);
            console.log('All steps hidden');
          }
          return newSteps;
        });
      }, index * hideDelay);
      animationTimeouts.current.push(timeout);
    });
  };

  const clearAllTimeouts = () => {
    animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.current = [];
  };

  const toggleStepExpansion = (index) => {
    setExpandedSteps(prev => {
      if (prev[index]) {
        return { ...prev, [index]: false };
      }
      return { [index]: true };
    });
  };

  const handleMouseEnter = (index) => {
    setHoveredStep(index);
  };

  const handleMouseLeave = () => {
    setHoveredStep(null);
  };

  return (
<div 
  ref={sectionRef}
  style={{ 
    backgroundColor: bodyBlack, 
    padding: isMobileView ? "10px 10px 20px 10px" : "20px 20px", 
    paddingBottom: isMobileView ? "-10px" : "10px", // Reduced bottom padding on mobile
    Height: isMobileView ? "auto" : "100vh", // Auto height on mobile
    position: "relative",
    overflow: "hidden"
  }}
>
      {/* Header is always visible when section is in view */}
      <WorkProcessHeader isVisible={isSectionInView} />
      
      <DesktopProcessFlow
        isSectionInView={isSectionInView}
        visibleSteps={visibleSteps}
        hoveredStep={hoveredStep}
        animationStarted={animationStarted}
        processSteps={processSteps}
        shouldAnimateOut={shouldAnimateOut}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      <MobileProcessFlow
        isSectionInView={isSectionInView}
        visibleSteps={visibleSteps}
        hoveredStep={hoveredStep}
        animationStarted={animationStarted}
        processSteps={processSteps}
        expandedSteps={expandedSteps}
        shouldAnimateOut={shouldAnimateOut}
        onStepClick={toggleStepExpansion}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      <BackgroundElements isVisible={isSectionInView} />

      <CSSAnimations />
    </div>
  );
};

const CSSAnimations = () => (
  <style>
    {`
      @keyframes dashAnimation {
        to {
          stroke-dashoffset: -40;
        }
      }
      
      .work-process-title {
        color: var(--color-white);
        font-weight: bold;
        margin-bottom: 1rem;
      }
      
      .work-subtitle {
        font-size: 1.1rem;
      }

      /* Media queries for responsive font sizes */
      @media (max-width: 768px) {
        .work-process-title {
          font-size: 1.5rem;
        }
        .work-subtitle {
          font-size: 1rem;
        }
      }

      @keyframes connectionPointPulse {
        0% {
          transform: scale(1);
          opacity: 0.8;
          stroke-width: 0.5;
        }
        50% {
          transform: scale(1.5);
          opacity: 0.4;
          stroke-width: 1;
        }
        100% {
          transform: scale(2);
          opacity: 0;
          stroke-width: 1.5;
        }
      }

      .connection-point-pulse {
        animation: connectionPointPulse 1s ease-out forwards;
      }

      /* Smooth fade transitions for steps */
      .step-fade-in {
        animation: fadeIn 0.8s ease-out forwards;
      }

      .step-fade-out {
        animation: fadeOut 0.6s ease-in forwards;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fadeOut {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(-20px);
        }
      }

     /* Mobile-specific animations with faster duration */
@media (max-width: 992px) {
  .step-fade-in {
    animation: fadeIn 0.5s ease-out forwards;
  }
  
  .step-fade-out {
    animation: fadeOut 0.4s ease-in forwards;
  }
}
        
    `}
  </style>
);

export default WorkProcessComponent;